import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import "./BestBloggerToday.css";
import BannerAds from "./BannerAds";

function BestBloggerToday() {
  const [topBloggers, setTopBloggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("today");
  const [timeLeft, setTimeLeft] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const navigate = useNavigate();

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  /* ─── PARTICLE CANVAS ─── */
  const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa", "#fbbf24"];

  const makeParticle = useCallback((W, H) => ({
    x: Math.random() * W,
    y: Math.random() * H,
    vx: (Math.random() - 0.5) * 0.4,
    vy: (Math.random() - 0.5) * 0.4,
    r: Math.random() * 1.8 + 0.4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    alpha: Math.random() * 0.6 + 0.2,
    pulse: Math.random() * Math.PI * 2,
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root) return;
    const ctx = canvas.getContext("2d");
    let W = root.offsetWidth;
    let H = Math.max(root.offsetHeight, window.innerHeight);
    let particles = [];

    const resize = () => {
      W = root.offsetWidth;
      H = Math.max(root.offsetHeight, window.innerHeight);
      canvas.width = W;
      canvas.height = H;
    };
    resize();
    particles = Array.from({ length: 90 }, () => makeParticle(W, H));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.018;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const pa = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pa;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = particles[i].color;
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - dist / 100) * 0.12;
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    const ro = new ResizeObserver(resize);
    ro.observe(root);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  /* ─── FETCH ─── */
  const fetchTop = async (type) => {
    try {
      const url =
        type === "week"
          ? `${BASE_URL}/api/reward/top-week`
          : `${BASE_URL}/api/reward/top-today`;
      const res = await axios.get(url);
      setTopBloggers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log(err);
      setTopBloggers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTop(mode); }, [mode]);
  useEffect(() => {
    const i = setInterval(() => fetchTop(mode), 5000);
    return () => clearInterval(i);
  }, [mode]);

/* ─── TIMER ─── */
useEffect(() => {
  const timer = setInterval(() => {
    const now = new Date();

    // Next reward: 1st day of next month at 6 PM
    const targetTime = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      1,
      18,
      0,
      0,
      0
    );

    const rewardEnd = new Date(targetTime.getTime() + 5 * 60 * 1000);

    if (now >= targetTime && now <= rewardEnd) {
      setTimeLeft("🎉 Reward Time!");
      return;
    }

    const distance = targetTime - now;

    const d = Math.floor(distance / (1000 * 60 * 60 * 24));
    const h = Math.floor((distance / (1000 * 60 * 60)) % 24);
    const m = Math.floor((distance / (1000 * 60)) % 60);
    const s = Math.floor((distance / 1000) % 60);

    setTimeLeft(`${d}d ${h}h ${m}m ${s}s`);
  }, 1000);

  return () => clearInterval(timer);
}, []);

  const goProfile = (author) => navigate(`/authorprofile/${author}`);

  const getInitials = (name) =>
    (name || "?").split("_").map((p) => p[0]).join("").toUpperCase().slice(0, 2);

  const RANK_META = [
    { icon: "🥇", glow: "#fbbf24" },
    { icon: "🥈", glow: "#94a3b8" },
    { icon: "🥉", glow: "#cd7c3a" },
  ];

  const PRIZES = [
    { rank: "🥇 1st", amount: "₹2500", color: "#fbbf24" },
    { rank: "🥈 2nd", amount: "₹1000", color: "#94a3b8" },
    { rank: "🥉 3rd", amount: "₹750", color: "#cd7c3a" },
    { rank: "4th", amount: "₹500", color: "#a78bfa" },
    { rank: "5th", amount: "₹100",  color: "#00ffc8" },
  ];

  const sortedBloggers = mode === "today"
    ? [...topBloggers].sort((a, b) => b.totalLikes - a.totalLikes).slice(0, 10)
    : [...topBloggers].sort((a, b) => {
        const rA = a.totalReward || 0, rB = b.totalReward || 0;
        if (rB !== rA) return rB - rA;
        if (b.totalLikes !== a.totalLikes) return b.totalLikes - a.totalLikes;
        return b.totalPosts - a.totalPosts;
      });

  return (
    <div className="bb-root" ref={rootRef}>
      <canvas className="bb-canvas" ref={canvasRef} />
      <div className="bb-aurora" />
      <video autoPlay loop muted playsInline className="bb-bg-video">
        <source src="/radhakrishna.mp4" type="video/mp4" />
      </video>

      <Navbar />

      <div className="bb-container">

        {/* ─── HEADER ─── */}
        <div className="bb-header">
          <BannerAds/>
          <div className="bb-header-label">✦ live rankings</div>
          <h1>{mode === "today" ? "Top Bloggers Today" : "This Month's Top Bloggers"}</h1>
          <p>{mode === "today" ? "ranked by total likes · refreshes every 5s" : "ranked by reward · likes · posts"}</p>
        </div>

        {/* ─── TOGGLE ─── */}
        <div className="bb-toggle-row">
          <button
            className={`bb-toggle-btn${mode === "today" ? " bb-toggle-active" : ""}`}
            onClick={() => setMode("today")}
          >Today</button>
          <button
            className={`bb-toggle-btn${mode === "week" ? " bb-toggle-active-week" : ""}`}
            onClick={() => setMode("week")}
          >This Month</button>
          <button className="bb-toggle-btn bb-toggle-reward" onClick={() => navigate("/pr")}>
            🎁 Reward
          </button>
        </div>

        {/* ─── WEEK BANNER ─── */}
        {mode === "week" && (
          <div className="bb-banner">
            <div className="bb-banner-scanline" />
            <div className="bb-banner-glow" />
            <div className="bb-banner-header-label">🏆 Monthly Contest</div>
            <h2 className="bb-banner-title">Top 5 Bloggers Win Real Rewards</h2>
            <p className="bb-banner-sub">Hurry up! Compete now and claim your prize</p>

            <div className="bb-prizes">
              {PRIZES.map((p) => (
                <div className="bb-prize-card" key={p.rank} style={{ "--prize-color": p.color }}>
                  <span className="bb-prize-rank">{p.rank}</span>
                  <span className="bb-prize-amount">{p.amount}</span>
                </div>
              ))}
            </div>

            <div className="bb-timer">
              <span className="bb-timer-label">Next Reward In</span>
              <span className="bb-timer-value">{timeLeft}</span>
            </div>
          </div>
        )}

      {/* ─── TABLE ─── */}
{loading ? (
  <div className="bb-loading">
    <span className="bb-dot" /><span className="bb-dot" /><span className="bb-dot" />
  </div>
) : sortedBloggers.length > 0 ? (
  <div className="bb-table-wrap">
    <div className="bb-table-scanline" />
    <table className="bb-table">
      <thead>
        <tr>
          <th>Rank</th>
          <th>Author</th>
          <th>Posts</th>
          <th>Likes</th>
          <th>🔥 Streak</th> {/* ✅ ADDED HERE */}
          {mode === "week" && <th>Reward (₹)</th>}
        </tr>
      </thead>

      <tbody>
        {sortedBloggers.map((b, index) => (
          <tr
            key={index}
            className={`bb-row${index < 3 ? " bb-top3" : ""}${
              hoveredRow === index ? " bb-row-hovered" : ""
            }`}
            onClick={() => goProfile(b.author)}
            onMouseEnter={() => setHoveredRow(index)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            {/* Rank */}
            <td>
              <span className="bb-rank">
                {index < 3 ? RANK_META[index].icon : `#${index + 1}`}
              </span>
            </td>

            {/* Author */}
            <td>
              <div className="bb-author-cell">
                <div className="bb-avatar">{getInitials(b.author)}</div>
                <strong>{b.author}</strong>
              </div>
            </td>

            {/* Posts */}
            <td>
              <span className="bb-stat-pill">{b.totalPosts}</span>
            </td>

            {/* Likes */}
            <td>
              <span className="bb-stat-pill bb-likes-pill">
                {b.totalLikes}
              </span>
            </td>

            {/* 🔥 STREAK COLUMN */}
            <td>
              <span className="bb-streak-pill">
                🔥 {b.streakCount || 0}
              </span>
            </td>

            {/* Reward */}
            {mode === "week" && (
              <td>
                <span className="bb-reward-pill">
                  ₹{b.totalReward || 0}
                </span>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
) : (
  <div className="bb-empty">[ NO BLOGGERS FOUND ]</div>
)}
        {/* ─── WEEK NOTE ─── */}
        {mode === "week" && (
          <div className="bb-note-card">
            <div className="bb-note-scanline" />
            <div className="bb-note-header-label">📋 Rules & Eligibility</div>
            <ul className="bb-note-list">
              <li>Every Month 1'st at <strong>6 PM</strong> we announce Monthly winners.</li>
              <li>Top bloggers receive rewards after manual verification of posts, likes, and user activity.</li>
              <li>To be eligible: stay active for at least <strong>12 hours</strong>, upload at least <strong>15 blogs</strong>, and earn at least <strong>1000 total likes</strong>  during the contest Month. and maintain at least <strong>149 streaks</strong></li>
              <li>Winners receive email only from <strong>theblogssphere@gmail.com</strong> </li>
              <li>We never ask for extra personal details. Do not share your account with anyone.</li>
            
            </ul>
            <p className="bb-terms-row">
              For full rules visit{" "}
              <span className="bb-terms-link" onClick={() => navigate("/terms")}>
                Terms &amp; Conditions →
              </span>
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

export default BestBloggerToday;